import { h } from 'preact'
import ConfirmationModal from '../ui-components/ConfirmationModal'
import InputGroup from '../ui-components/InputGroup'
import ButtonControl from '../button-control'
import ZendeskLogo from './assets/zendeskLogo.js'
import IntegrationHeader from './IntegrationHeader'
import Panel from '../panel'
import Switcher from '../control/Switcher'
import Tooltip from '../ui-components/Tooltip'
import Loader from '../ui-components/Loader'
import SimpleCollapsible from '../ui-components/SimpleCollapsible'


export interface ZendeskData {
  apiToken: string,
  subdomain: string,
  agentEmail: string,
  apiTokenError: string,
  subdomainError: string,
  agentEmailError: string,
  showConfirmationMessage: boolean,
  isTicketEscalationActive: boolean,
  isFederatedSearchActive: boolean,
  syncInProgress: boolean,
  enablingInProgress: boolean,
  hasInstalledCredentials: boolean,
  locale: string,
  searchableCategories: string[]
}

interface Props {
  data: ZendeskData,
  onDelete: () => void,
  onHideConfirmationMessage: () => void,
  toggleZendeskTicketCreation: OnChangeCallback,
  toggleFederatedSearch: OnChangeCallback
  onApiTokenChanged: OnChangeCallback,
  onSubdomainChanged: OnChangeCallback,
  onAgentEmailChanged: OnChangeCallback,
  onShowConfirmationMessage: OnClickCallback,
  onLocaleChanged: OnChangeCallback,
  onSearchableCategoriesChanged: OnChangeCallback,
  onInstall: OnClickCallback,
  onSync: OnClickCallback,
  isIntegrationsHomepage?: boolean
}

const Zendesk = (props: Props) => {
  const {
    apiToken,
    subdomain,
    agentEmail,
    apiTokenError,
    subdomainError,
    agentEmailError,
    syncInProgress,
    enablingInProgress,
    showConfirmationMessage,
    isTicketEscalationActive,
    isFederatedSearchActive,
    hasInstalledCredentials,
    locale,
    searchableCategories
  } = props.data

  const getDeleteConfirmationButtons = () => {
    return [
      {
        text: 'Confirm',
        class: 'btn btn-danger qa-confirm',
        showLoading: false,
        callback: props.onDelete
      },
      {
        text: 'Cancel',
        class: 'btn btn-default',
        showLoading: false,
        callback: props.onHideConfirmationMessage
      }
    ]
  }

  if (props.isIntegrationsHomepage) {
    return (
      <Panel title={<IntegrationHeader isInstalled={hasInstalledCredentials} name='Zendesk' description='Use this integration to create Zendesk tickets from community posts and to enable federated search'><ZendeskLogo width={72} height={72} /></IntegrationHeader>}></Panel>
    )
  }

  return (
    <Panel title='Integration setup'>
      {
        hasInstalledCredentials ? (
          <div className='integrations-zendesk__toggle-buttons'>
            <div className='form-group'>
              <strong>Ticket escalation</strong>
              <div className='integrations-zendesk__ticket-escalation-settings clearfix'>
                <Switcher
                  on={isTicketEscalationActive}
                  label='Enable ticket escalation from control'
                  onChange={props.toggleZendeskTicketCreation} />
              </div>
            </div>
            <div className='form-group'>
              <strong>Federated search</strong>
              <div className='integrations-zendesk__federated-search-settings clearfix'>
                <Switcher
                  on={isFederatedSearchActive}
                  label='Enable federated search'
                  onChange={props.toggleFederatedSearch} />

                {
                  isFederatedSearchActive ? (
                    <div>
                      <ButtonControl
                        variant='secondary'
                        title='Synchronize articles'
                        isLoading={syncInProgress}
                        onClick={props.onSync} />
                      <Tooltip
                        trigger={<i className='fa fa-question-circle' />}
                        customClasses='integrations-zendesk__sync-tooltip'
                        tooltipMessage='Search index from Zendesk are updated every day. Click this button to trigger a full synchronisation if needed.'
                      />
                    </div>
                  ) : null
                }
                {
                  enablingInProgress && <Loader />
                }
              </div>
            </div>
            <SimpleCollapsible title='Advanced filters'>
              <div className='integrations-zendesk__federated-advanced-search-settings clearfix'>
                {
                  isFederatedSearchActive &&
                  <div className='integrations-zendesk__federated-advanced-search-settings-note'>
                    Note: If you would like to edit filters, please disable federated search first.
                  </div>
                }
                <div className='form-group'>
                  <InputGroup title='Filter by language'
                    type='text'
                    qaClasses='qa-search-locale'
                    placeholder={`${!isFederatedSearchActive ? 'e.g. en-us' : ''}`}
                    meta='Enter locale parameter of the language that you want to show in federated search'
                    disabled={isFederatedSearchActive}
                    value={locale}
                    onChange={props.onLocaleChanged}/>
                  <InputGroup title='Filter by category'
                    type='text'
                    qaClasses='qa-search-searchable-categories'
                    placeholder={`${!isFederatedSearchActive ? 'e.g. 123456, 123457': ''}`}
                    meta='Enter the category IDs of the categories that you want to show in federated search, separated by comma'
                    disabled={isFederatedSearchActive}
                    value={searchableCategories}
                    onChange={props.onSearchableCategoriesChanged}/>
                </div>
              </div>
            </SimpleCollapsible>
          </div>
        ) : null
      }
      { hasInstalledCredentials && <hr /> }
      <InputGroup title='API token *'
        type='text'
        required
        qaClasses='qa-api-token-input'
        disabled={hasInstalledCredentials}
        value={apiToken}
        onChange={props.onApiTokenChanged}
        error={apiTokenError} />
      <div class='integrations-zendesk__domain-wrapper'>
        <div class={`integrations-zendesk__domain ${hasInstalledCredentials ? 'integrations-zendesk__domain--disabled' : ''} ${subdomainError ? 'integrations-zendesk__domain--error' : ''}`}>
          .zendesk.com
        </div>
        <span class='integrations-zendesk__domain-input'>
          <InputGroup title='Subdomain *'
            type='text'
            required
            qaClasses='qa-subdomain-input'
            disabled={hasInstalledCredentials}
            value={subdomain}
            onChange={props.onSubdomainChanged}
            error={subdomainError} />
        </span>
      </div>
      <InputGroup title='Agent Email *'
        type='email'
        required
        qaClasses='qa-agent-email-input'
        disabled={hasInstalledCredentials}
        value={agentEmail}
        onChange={props.onAgentEmailChanged}
        error={agentEmailError} />
      {hasInstalledCredentials
        ? <ButtonControl variant='warning' class={'qa-delete'} title='Uninstall and delete credentials' onClick={props.onShowConfirmationMessage} />
        : <ButtonControl variant='primary' title='Install' class={'qa-submit'} onClick={props.onInstall} />
      }
      <ConfirmationModal
        enableModal={showConfirmationMessage}
        title='Uninstall and delete credentials?'
        message='Your team will no longer be able to create Zendesk tickets from community posts. Posts that were already escalated will still show links to the ticket.'
        buttons={getDeleteConfirmationButtons()}
        onClose={props.onHideConfirmationMessage}
      />
    </Panel>
  )
}

export default Zendesk
